import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  Table,
  Button,
  Menu,
  Dropdown,
  Badge,
  Tooltip,
  ConfigProvider,
} from 'antd';
import {
  ReadOutlined,
  DeleteOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import 'react-circular-progressbar/dist/styles.css';

import { setReportData, setLuoxUnits } from 'redux/actions';

import { Simple, Intermediate, Full } from 'components/tabs';
import {
  ChooseColumnsModal,
  ReportPreviewModal,
  DeleteLibraryItemModal,
  TableFilterModal,
  JsonDownloadDetailsModal,
  ImageDownloadOptionsModal,
  AddLibraryModal,
  ClearLibraryModal,
  UploadErrorModal,
  MultipleSpdxDownloadModal,
} from './libraryTableModals';

import './index.scss';
import { LoadingScreen } from 'components/loadingScreen';

export const colorBarValues = [
  '#b45c60',
  '#cb7761',
  '#cb824b',
  '#d7ad67',
  '#ac9a5d',
  '#919e60',
  '#678b5f',
  '#62b08f',
  '#7cbaba',
  '#2c797d',
  '#54778c',
  '#7189b1',
  '#988ca9',
  '#735876',
  '#8f6682',
  '#b97b8e',
];
export const getPreferenceLevel = (results) => {
  const rCsh1 = Math.round(results.local_chroma_shift[0]);
  const rf = Math.round(results.rf);
  const rg = Math.round(results.rg);

  let preferenceLevel;

  if (rf >= 78 && rg >= 95 && rCsh1 >= -1 && rCsh1 <= 15) {
    preferenceLevel = '1';
  } else if (rf >= 75 && rg >= 92 && rCsh1 >= -7 && rCsh1 <= 19) {
    preferenceLevel = '2';
  } else if (rf >= 70 && rg >= 89 && rCsh1 >= -12 && rCsh1 <= 23) {
    preferenceLevel = '3';
  } else {
    preferenceLevel = '-';
  }

  return preferenceLevel;
};

export const getVividnessLevel = (results) => {
  const rCsh1 = Math.round(results.local_chroma_shift[0]);
  const rg = Math.round(results.rg);
  let vividnessLevel;

  if (rg >= 118 && rCsh1 >= 15) {
    vividnessLevel = '1';
  } else if (rg >= 110 && rCsh1 >= 6) {
    vividnessLevel = '2';
  } else if (rg >= 100 && rCsh1 >= 0) {
    vividnessLevel = '3';
  } else {
    vividnessLevel = '-';
  }

  return vividnessLevel;
};

export const getFidelityLevel = (results) => {
  const rf = Math.round(results.rf);
  const lcf = results.local_color_fidelity[0];
  let fidelityLevel;

  if (rf >= 95) {
    fidelityLevel = '1';
  } else if (rf >= 90 && Math.round(lcf) >= 90) {
    fidelityLevel = '2';
  } else if (rf >= 85 && Math.round(lcf) >= 85) {
    fidelityLevel = '3';
  } else {
    fidelityLevel = '-';
  }

  return fidelityLevel;
};

const { SubMenu } = Menu;

const LibraryTable = ({ libraryData, libraryType, renderTableEmpty }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const showGlobalSpinner = useSelector(
    (state) => state.dataReducers.showGlobalSpinner
  );
  // const excelUploadErrors = useSelector(
  //   (state) => state.dataReducers.excelUploadErrors
  // );
  const showUploadError = useSelector(
    (state) => state.dataReducers.showUploadError
  );

  // const [searchText, setSearchText] = useState("");
  // const [searchedColumn, setSearchedColumn] = useState("");
  // const [initialTableLoad, setInitialTableLoad] = useState(true);
  const [showChooseColumnsModal, setShowChooseColumnsModal] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState(null);
  const [modalReportData, setModalReportData] = useState({});
  const [reportType, setReportType] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [clearLibrary, setClearLibrary] = useState(false);
  const [addLibrary, setAddLibrary] = useState(false);
  const [tableSpinner, setTableSpinner] = useState(false);
  const [openExcelUploadErrors, setOpenExcelUploadErrors] = useState(false);

  const [tableFiltering, setTableFiltering] = useState(false);
  const [tableFilterData, setTableFilterData] = useState({});
  const [tableDataAfterFiltersAndSorters, setTableDataAfterFiltersAndSorters] =
    useState({});
  const [jsonDownloadModal, setJsonDownloadModal] = useState(false);
  const [jsonDownloadName, setJsonDownloadName] = useState(
    libraryType === 'user'
      ? 'Spectral Calculator User Library'
      : 'Spectral Calculator Example Library'
  );
  const [jsonDownloadType, setJsonDownloadType] = useState('full');

  // Batch image download state
  const [imageDownloadType, setImageDownloadType] = useState([
    'simple',
    'intermediate',
    'full',
  ]);
  const [imageDownloadModal, setImageDownloadModal] = useState(false);
  const [imageDownloadName, setImageDownloadName] = useState(['model']);
  const [imageDownloadIds, setImageDownloadIds] = useState([]);
  const [imageDownloadStart, setImageDownloadStart] = useState(false);

  const [spdxDownloadModal, setSpdxDownloadModal] = useState(false);
  const [spdxDownloadName, setSpdxDownloadName] = useState(
    'Spectral-calculator-SPDx-Files'
  );
  const [spdxDownloadType, setSpdxDownloadType] = useState('full');

  useEffect(() => {
    if (showUploadError === 'show') {
      setOpenExcelUploadErrors(true);
    }
  }, [showUploadError]);

  const handleImageDownloadIds = (selectedRowKeys) => {
    const filteredSelectedRowKeys = selectedRowKeys.filter((rowKey) => rowKey);
    setImageDownloadIds(
      JSON.stringify(filteredSelectedRowKeys) ===
        JSON.stringify(imageDownloadIds)
        ? []
        : filteredSelectedRowKeys
    );
  };

  const showReportModal = useCallback((reportType, reportData, metadata) => {
    setReportType(reportType);
    setModalReportData({ ...reportData, metadata });
  }, []);

  const generateReport = (reportData) => {
    dispatch(setLuoxUnits(reportData.luoxData?.luoxUnits));
    dispatch(setReportData(reportData));
    history.push({
      pathname: '/results',
    });
  };

  //   const getColumnSearchProps = dataIndex => ({
  //     filterDropdown: ({
  //       setSelectedKeys,
  //       selectedKeys,
  //       confirm,
  //       clearFilters
  //     }) => (
  //       <div style={{ padding: 8 }}>
  //         <Input
  //           ref={node => {
  //             this.searchInput = node;
  //           }}
  //           placeholder={`Search ${dataIndex}`}
  //           value={selectedKeys[0]}
  //           onChange={e =>
  //             setSelectedKeys(e.target.value ? [e.target.value] : [])
  //           }
  //           onPressEnter={(selectedKeys, confirm, dataIndex) =>
  //             {
  //                 confirm();
  //                 setSearchText(selectedKeys[0]);
  //                 setSearchedColumn(dataIndex)
  //             }
  //           }
  //           style={{ width: 188, marginBottom: 8, display: "block" }}
  //         />
  //         <Space>
  //           <Button
  //             type="primary"
  //             onClick={() => this.handleSearch(selectedKeys, confirm, dataIndex)}
  //             icon={<SearchOutlined />}
  //             size="small"
  //             style={{ width: 90 }}
  //           >
  //             Search
  //           </Button>
  //           <Button
  //             onClick={(clearFilters) => {
  //                 clearFilters();
  //                 setSearchText("")
  //             }}
  //             size="small"
  //             style={{ width: 90 }}
  //           >
  //             Reset
  //           </Button>
  //         </Space>
  //       </div>
  //     ),
  //     filterIcon: filtered => (
  //       <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
  //     ),
  //     onFilter: (value, record) =>
  //       record[dataIndex]
  //         ? record[dataIndex]
  //             .toString()
  //             .toLowerCase()
  //             .includes(value.toLowerCase())
  //         : "",
  //     onFilterDropdownVisibleChange: visible => {
  //       if (visible) {
  //         setTimeout(() => this.searchInput.select(), 100);
  //       }
  //     },
  //     render: text =>
  //       this.state.searchedColumn === dataIndex ? (
  //         <Highlighter
  //           highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
  //           searchWords={[this.state.searchText]}
  //           autoEscape
  //           textToHighlight={text ? text.toString() : ""}
  //         />
  //       ) : (
  //         text
  //       )
  //   });

  //////////// Generating table columns ////////////

  // const columnsInSort = useMemo(() => {
  //   if (tableDataAfterFiltersAndSorters.sorter?.length) {
  //     return tableDataAfterFiltersAndSorters.sorter.map(
  //       (sorter) => sorter.field
  //     );
  //   }
  // }, [tableDataAfterFiltersAndSorters]);

  const columns = [
    {
      title: 'Unique Identifier',
      dataIndex: 'identifier',
      key: 'identifier',
      className: 'metadata-field',
      width: 150,
      fixed: 'left',
    },
    {
      title: 'Make or Manufacturer',
      dataIndex: 'make',
      key: 'make',
      className: 'metadata-field',
      width: 100,
      // ...getColumnSearchProps("make")
    },
    {
      title: 'Model or Catalog Number or ID',
      dataIndex: 'model',
      key: 'model',
      className: 'metadata-field',
      width: 100,
      filters: [
        { text: 'CIE', value: 'CIE' },
        { text: 'LED Hybrid Blue Pump', value: 'LED Hybrid Blue Pump' },
        { text: 'RGB', value: 'RGB' },
        { text: 'RGBA', value: 'RGBA' },
        {
          text: 'LED Phosphor Blue Pump',
          value: 'LED Phosphor Blue Pump',
        },
        {
          text: 'LED Phosphor Violet Pump',
          value: 'LED Phosphor Violet Pump',
        },
      ],
      onFilter: (value, record) => record.model.indexOf(value) === 0,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      className: 'metadata-field',
      width: 100,
    },
    {
      title: 'Document Creator',
      dataIndex: 'creator',
      key: 'creator',
      className: 'metadata-field',
      width: 100,
    },
    {
      title: 'Measurement Equipment',
      dataIndex: 'measurement',
      key: 'measurement',
      className: 'metadata-field',
      width: 100,
    },
    {
      title: 'Laboratory',
      dataIndex: 'laboratory',
      key: 'laboratory',
      className: 'metadata-field',
      width: 100,
    },
    {
      title: 'Report Number',
      dataIndex: 'reportNumber',
      key: 'reportNumber',
      width: 100,
    },
    {
      title: 'Report Date',
      dataIndex: 'reportDate',
      key: 'reportDate',
      width: 100,
    },
    {
      title: 'Creation Date',
      dataIndex: 'creationDate',
      key: 'creationDate',
      width: 100,
    },
    {
      title: 'Comments',
      dataIndex: 'comments',
      key: 'comments',
      className: 'metadata-field',
      width: 100,
    },
    {
      title: 'Spectral Quantity (e.g., W/nm)',
      dataIndex: 'spectralQuantity',
      key: 'spectralQuantity',
      width: 100,
    },
    {
      title: 'CCT (K)',
      dataIndex: 'cct',
      key: 'cct',
      width: 100,
      // defaultSortOrder:
      //   JSON.parse(sessionStorage.getItem('table-sort'))?.columnKey === 'cct'
      //     ? JSON.parse(sessionStorage.getItem('table-sort'))?.order
      //     : '',
      sorter: (a, b) => {
        if (typeof a.cct.props.children === 'object') {
          return (
            a.cct.props.children.props.children -
            b.cct.props.children.props.children
          );
        } else {
          return a.cct.props.children - b.cct.props.children;
        }
      },
    },
    {
      title: (
        <span>
          <i>D</i>
          <sub>uv</sub>
        </span>
      ),
      dataIndex: 'duv',
      key: 'duv',
      matchColumnTitle: 'Duv',
      width: 100,
      sorter: (a, b) => a.duv - b.duv,
    },
    {
      title: (
        <span>
          Fidelity Index (<i>R</i>
          <sub>f</sub>)
        </span>
      ),
      dataIndex: 'rf',
      key: 'rf',
      matchColumnTitle: 'Rf',
      width: 100,
      sorter: (a, b) => a.rf - b.rf,
    },
    {
      title: (
        <span>
          Gamut Index (<i>R</i>
          <sub>g</sub>)
        </span>
      ),
      dataIndex: 'rg',
      key: 'rg',
      matchColumnTitle: 'Rg',
      width: 100,
      sorter: (a, b) => a.rg - b.rg,
    },
    {
      title: (
        <span>
          Local Chroma Shift (<i>R</i>
          <sub>cs</sub>
          <sub>
            ,h
            <i>j</i>
          </sub>
          )
        </span>
      ),
      dataIndex: 'lcs',
      key: 'lcs',
      matchColumnTitle: 'Local Chroma Shift (RCS,hj)',
      width: 150,
      render: (record) =>
        typeof record === 'object' ? (
          record
        ) : (
          <Tooltip
            placement='top'
            title='Use the plus at the left to expand and show values for all 16 hue-angle bins.'
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Badge
                count={1}
                style={{
                  backgroundColor: colorBarValues[0],
                  width: '30px',
                  marginLeft: '2rem',
                }}
              />
              <span style={{ margin: '0 1rem' }}>{Math.round(record)}%</span>
            </div>
          </Tooltip>
        ),
      sorter: (a, b) => a.lcs - b.lcs,
    },
    {
      title: (
        <span>
          Local Hue Shift (<i>R</i>
          <sub>hs</sub>
          <sub>
            ,h
            <i>j</i>
          </sub>
          )
        </span>
      ),
      dataIndex: 'lhs',
      key: 'lhs',
      matchColumnTitle: 'Local Hue Shift (Rhs,hj)',
      width: 150,
      render: (record) =>
        typeof record === 'object' ? (
          record
        ) : (
          <Tooltip
            placement='top'
            title='Use the plus at the left to expand and show values for all 16 hue-angle bins.'
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Badge
                count={1}
                style={{
                  backgroundColor: colorBarValues[0],
                  width: '30px',
                  marginLeft: '2rem',
                }}
              />
              <span style={{ margin: '0 1rem' }}>{record}</span>
            </div>
          </Tooltip>
        ),
      sorter: (a, b) => a.lhs - b.lhs,
    },
    {
      title: (
        <span>
          Local Color Fidelity (<i>R</i>
          <sub>f</sub>
          <sub>
            ,h
            <i>j</i>
          </sub>
          )
        </span>
      ),
      dataIndex: 'lcf',
      key: 'lcf',
      matchColumnTitle: 'Local Color Fidelity (Rf,hj)',
      width: 150,
      render: (record) =>
        typeof record === 'object' ? (
          record
        ) : (
          <Tooltip
            placement='top'
            title='Use the plus at the left to expand and show values for all 16 hue-angle bins.'
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Badge
                count={1}
                style={{
                  backgroundColor: colorBarValues[0],
                  width: '30px',
                  marginLeft: '2rem',
                }}
              />
              <span style={{ margin: '0 1rem' }}>{record}</span>
            </div>
          </Tooltip>
        ),
      sorter: (a, b) => a.lcf - b.lcf,
    },
    {
      title: (
        <span>
          Sample Color Fidelity (<i>R</i>
          <sub>
            f,ces<i>i</i>
          </sub>
          )
        </span>
      ),
      dataIndex: 'scf',
      key: 'scf',
      matchColumnTitle: 'Sample Color Fidelity (Rf,CESi)',
      width: 100,
    },
    {
      title: 'P Level',
      dataIndex: 'preferenceLevel',
      key: 'preferenceLevel',
      width: 100,
      // filters: [
      //   { text: '1', value: '1' },
      //   { text: '2', value: '2' },
      //   { text: '3', value: '3' },
      // ],
      // onFilter: (value, record) => record.preferenceLevel.indexOf(value) === 0,
      sorter: (a, b) => {
        const aPreferenceLevel =
          a.preferenceLevel !== '-' ? a.preferenceLevel : 0;
        const bPreferenceLevel =
          b.preferenceLevel !== '-' ? b.preferenceLevel : 0;

        return aPreferenceLevel - bPreferenceLevel;
      },
    },
    {
      title: 'V Level',
      dataIndex: 'vividnessLevel',
      key: 'vividnessLevel',
      width: 100,
      // filters: [
      //   { text: '1', value: '1' },
      //   { text: '2', value: '2' },
      //   { text: '3', value: '3' },
      // ],
      // onFilter: (value, record) => record.vividnessLevel.indexOf(value) === 0,
      sorter: (a, b) => {
        const aVividnessLevel = a.vividnessLevel !== '-' ? a.vividnessLevel : 0;
        const bVividnessLevel = b.vividnessLevel !== '-' ? b.vividnessLevel : 0;

        return aVividnessLevel - bVividnessLevel;
      },
    },
    {
      title: 'F Level',
      dataIndex: 'fidelityLevel',
      key: 'fidelityLevel',
      width: 100,
      // filters: [
      //   { text: '1', value: '1' },
      //   { text: '2', value: '2' },
      //   { text: '3', value: '3' },
      // ],
      // onFilter: (value, record) => record.fidelityLevel.indexOf(value) === 0,
      sorter: (a, b) => {
        const aFidelityLevel = a.fidelityLevel !== '-' ? a.fidelityLevel : 0;
        const bFidelityLevel = b.fidelityLevel !== '-' ? b.fidelityLevel : 0;

        return aFidelityLevel - bFidelityLevel;
      },
    },
    {
      title: 'Luminance/Illuminance (Type)',
      dataIndex: 'luminanceType',
      key: 'luminanceType',
      width: 180,
      // fixed: "left",
      sorter: {
        compare: (a, b) => a.luminanceType?.localeCompare(b.luminanceType),
        multiple: 2,
      },
      // render: (value) => <Tag>{value}</Tag>,
    },
    {
      title: 'Luminance/Illuminance',
      dataIndex: 'luminanceValue',
      key: 'luminanceValue',
      width: 180,
      sorter: {
        compare: (a, b) => a.luminanceValue - b.luminanceValue,
        multiple: 1,
      },
    },
    // {
    //   title: (
    //     <span>
    //       CIE 1931 <i>x</i>
    //     </span>
    //   ),
    //   dataIndex: 'cie1931x',
    //   key: 'cie1931x',
    //   matchColumnTitle: 'CIE 1931 xy chromaticity (x)',
    //   width: 110,
    //   sorter: {
    //     compare: (a, b) => a.cie1931x - b.cie1931x,
    //     multiple: 1,
    //   },
    // },
    // {
    //   title: (
    //     <span>
    //       CIE 1931 <i>y</i>
    //     </span>
    //   ),
    //   dataIndex: 'cie1931y',
    //   key: 'cie1931y',
    //   matchColumnTitle: 'CIE 1931 xy chromaticity (y)',
    //   width: 110,
    //   sorter: {
    //     compare: (a, b) => a.cie1931y - b.cie1931y,
    //     multiple: 1,
    //   },
    // },
    // {
    //   title: (
    //     <span>
    //       CIE 1964 <i>x₁₀</i>
    //     </span>
    //   ),
    //   dataIndex: 'cie1964x',
    //   key: 'cie1964x',
    //   matchColumnTitle: 'CIE 1964 x₁₀y₁₀ chromaticity (x₁₀)',
    //   width: 110,
    //   sorter: {
    //     compare: (a, b) => a.cie1964x - b.cie1964x,
    //     multiple: 1,
    //   },
    // },
    // {
    //   title: (
    //     <span>
    //       CIE 1964 <i>y₁₀</i>
    //     </span>
    //   ),
    //   dataIndex: 'cie1964y',
    //   key: 'cie1964y',
    //   matchColumnTitle: 'CIE 1964 x₁₀y₁₀ chromaticity (y₁₀)',
    //   width: 110,
    //   sorter: {
    //     compare: (a, b) => a.cie1964y - b.cie1964y,
    //     multiple: 1,
    //   },
    // },
    {
      title: 'S-cone-opic (mW.m⁻²)',
      dataIndex: 'sConeIrradiance',
      key: 'sConeIrradiance',
      width: 110,
      sorter: {
        compare: (a, b) => a.sConeIrradiance - b.sConeIrradiance,
        multiple: 1,
      },
    },
    {
      title: 'M-cone-opic (mW.m⁻²)',
      dataIndex: 'mConeIrradiance',
      key: 'mConeIrradiance',
      width: 110,
      sorter: {
        compare: (a, b) => a.mConeIrradiance - b.mConeIrradiance,
        multiple: 1,
      },
    },
    {
      title: 'L-cone-opic (mW.m⁻²)',
      dataIndex: 'lConeIrradiance',
      key: 'lConeIrradiance',
      width: 110,
      sorter: {
        compare: (a, b) => a.lConeIrradiance - b.lConeIrradiance,
        multiple: 1,
      },
    },
    {
      title: 'Rhodopic (mW.m⁻²)',
      dataIndex: 'rhodopicIrradiance',
      key: 'rhodopicIrradiance',
      width: 110,
      sorter: {
        compare: (a, b) => a.rhodopicIrradiance - b.rhodopicIrradiance,
        multiple: 1,
      },
    },
    {
      title: 'Melanopic (mW.m⁻²)',
      dataIndex: 'melanopicIrradiance',
      key: 'melanopicIrradiance',
      width: 110,
      sorter: {
        compare: (a, b) => a.melanopicIrradiance - b.melanopicIrradiance,
        multiple: 1,
      },
    },
    {
      title: 'S-cone-opic EDI',
      dataIndex: 'sConeOpicEdl',
      key: 'sConeOpicEdl',
      width: 110,
      sorter: {
        compare: (a, b) => a.sConeOpicEdl - b.sConeOpicEdl,
        multiple: 1,
      },
    },
    {
      title: 'M-cone-opic EDI',
      dataIndex: 'mConeOpicEdl',
      key: 'mConeOpicEdl',
      width: 110,
      sorter: {
        compare: (a, b) => a.mConeOpicEdl - b.mConeOpicEdl,
        multiple: 1,
      },
    },
    {
      title: 'L-cone-opic EDI',
      dataIndex: 'lConeOpicEdl',
      key: 'lConeOpicEdl',
      width: 110,
      sorter: {
        compare: (a, b) => a.lConeOpicEdl - b.lConeOpicEdl,
        multiple: 1,
      },
    },
    {
      title: 'Rhodopic EDI',
      dataIndex: 'rhodopicEdl',
      key: 'rhodopicEdl',
      width: 110,
      sorter: {
        compare: (a, b) => a.rhodopicEdl - b.rhodopicEdl,
        multiple: 1,
      },
    },
    {
      title: 'Melanopic EDI',
      dataIndex: 'melanopicEdl',
      key: 'melanopicEdl',
      width: 110,
      sorter: {
        compare: (a, b) => a.melanopicEdl - b.melanopicEdl,
        multiple: 1,
      },
    },
    {
      title: 'S-cone-opic ELR',
      dataIndex: 'sConeOpicElr',
      key: 'sConeOpicElr',
      width: 110,
      sorter: {
        compare: (a, b) => a.sConeOpicElr - b.sConeOpicElr,
        multiple: 1,
      },
    },
    {
      title: 'M-cone-opic ELR',
      dataIndex: 'mConeOpicElr',
      key: 'mConeOpicElr',
      width: 110,
      sorter: {
        compare: (a, b) => a.mConeOpicElr - b.mConeOpicElr,
        multiple: 1,
      },
    },
    {
      title: 'L-cone-opic ELR',
      dataIndex: 'lConeOpicElr',
      key: 'lConeOpicElr',
      width: 110,
      sorter: {
        compare: (a, b) => a.lConeOpicElr - b.lConeOpicElr,
        multiple: 1,
      },
    },
    {
      title: 'Rhodopic ELR',
      dataIndex: 'rhodopicElr',
      key: 'rhodopicElr',
      width: 110,
      sorter: {
        compare: (a, b) => a.rhodopicElr - b.rhodopicElr,
        multiple: 1,
      },
    },
    {
      title: 'Melanopic ELR',
      dataIndex: 'melanopicElr',
      key: 'melanopicElr',
      width: 110,
      sorter: {
        compare: (a, b) => a.melanopicElr - b.melanopicElr,
        multiple: 1,
      },
    },
    { title: 'Delete', dataIndex: 'delete', key: 'delete', width: 100 },
    {
      title: 'Go to Results',
      dataIndex: 'report',
      key: 'report',
      width: 100,
      fixed: 'right',
    },
  ];

  // Memoizing data source
  const memoizedDataSource = useMemo(() => {
    const dataSource = [];
    libraryData.forEach((libraryItem) => {
      const { id, metadata, tm30, luoxData, warnings } = libraryItem;
      const { luoxOutput, luoxUnits } = luoxData || {};

      const { radianceOrIrradiance } = luoxUnits || {};
      const isRadiance = ['radiance', 'Radiance'].includes(
        radianceOrIrradiance
      );

      const {
        comments,
        creation_date,
        creator,
        description,
        identifier,
        laboratory,
        manufacturer,
        measurement,
        model,
        report_date,
        report_number,
        spectral_quantity,
      } = metadata || {};

      const reportMenu = (
        <Menu>
          <Menu.Item
            key='1'
            onClick={() =>
              showReportModal('simple', tm30.Numerical_Results, metadata)
            }
          >
            TM-30 Simple
          </Menu.Item>
          <Menu.Item
            key='2'
            onClick={() => {
              showReportModal('intermediate', tm30.Numerical_Results, metadata);
            }}
          >
            TM-30 Intermediate
          </Menu.Item>
          <Menu.Item
            key='3'
            onClick={() => {
              showReportModal('full', { tm30 }, metadata);
            }}
          >
            TM-30 Full
          </Menu.Item>
        </Menu>
      );

      const luoxTableData = luoxOutput
        ? {
            luminanceType: isRadiance
              ? 'Luminance [cd/m²]'
              : 'Illuminance [lx]',
            luminanceValue: luoxOutput.luminanceTotals[0].toFixed(4),
            cie1931x: luoxOutput.chromaticity31[0].x.toFixed(4),
            cie1931y: luoxOutput.chromaticity31[0].y.toFixed(4),
            cie1964x: luoxOutput.chromaticity64[0].x.toFixed(4),
            cie1964y: luoxOutput.chromaticity64[0].y.toFixed(4),
            sConeIrradiance: luoxOutput.sConeTotals[0].toFixed(4),
            mConeIrradiance: luoxOutput.mConeTotals[0].toFixed(4),
            lConeIrradiance: luoxOutput.lConeTotals[0].toFixed(4),
            rhodopicIrradiance: luoxOutput.rodTotals[0].toFixed(4),
            melanopicIrradiance: luoxOutput.melTotals[0].toFixed(4),
            sConeOpicEdl:
              luoxOutput.equivalentDaylightAlphaOpic.sc[0].toFixed(4),
            mConeOpicEdl:
              luoxOutput.equivalentDaylightAlphaOpic.mc[0].toFixed(4),
            lConeOpicEdl:
              luoxOutput.equivalentDaylightAlphaOpic.lc[0].toFixed(4),
            rhodopicEdl:
              luoxOutput.equivalentDaylightAlphaOpic.rh[0].toFixed(4),
            melanopicEdl:
              luoxOutput.equivalentDaylightAlphaOpic.mel[0].toFixed(4),
            sConeOpicElr: luoxOutput.alphaOpicEfficiency.sc[0].toFixed(4),
            mConeOpicElr: luoxOutput.alphaOpicEfficiency.mc[0].toFixed(4),
            lConeOpicElr: luoxOutput.alphaOpicEfficiency.lc[0].toFixed(4),
            rhodopicElr: luoxOutput.alphaOpicEfficiency.rh[0].toFixed(4),
            melanopicElr: luoxOutput.alphaOpicEfficiency.mel[0].toFixed(4),
          }
        : {
            luminance: '-',
            cie1931x: '-',
            cie1931y: '-',
            cie1964x: '-',
            cie1964y: '-',
            sConeIrradiance: '-',
            mConeIrradiance: '-',
            lConeIrradiance: '-',
            rhodopicIrradiance: '-',
            melanopicIrradiance: '-',
            sConeOpicEdl: '-',
            mConeOpicEdl: '-',
            lConeOpicEdl: '-',
            rhodopicEdl: '-',
            melanopicEdl: '-',
            sConeOpicElr: '-',
            mConeOpicElr: '-',
            lConeOpicElr: '-',
            rhodopicElr: '-',
            melanopicElr: '-',
          };

      const tableEntry = {
        key: id,
        make: manufacturer,
        model,
        description,
        creator,
        identifier,
        measurement,
        laboratory,
        reportNumber: report_number,
        reportDate: report_date,
        creationDate: creation_date,
        comments,
        spectralQuantity: spectral_quantity,
        cct: (
          <span
            style={{
              color:
                libraryType === 'user' && warnings?.length !== 0 && '#d6781e',
            }}
          >
            {libraryType === 'user' && warnings?.length !== 0 ? (
              <Tooltip
                placement='top'
                title={
                  <ul style={{ paddingLeft: '1.5rem' }}>
                    {warnings?.map((error, errorId) => (
                      <li key={errorId}>{error}</li>
                    ))}
                  </ul>
                }
              >
                {Math.round(tm30.Numerical_Results.CCT[0])}
              </Tooltip>
            ) : (
              Math.round(tm30.Numerical_Results.CCT[0])
            )}
          </span>
        ),
        duv: tm30.Numerical_Results.DUV[0].toFixed(4),
        rf: Math.round(tm30.Numerical_Results.rf),
        rg: Math.round(tm30.Numerical_Results.rg),
        lcs: tm30.Numerical_Results.local_chroma_shift[0],
        // (
        //   <div style={{ display: "flex", justifyContent: "center" }}>
        //     <Badge
        //       count={1}
        //       style={{
        //         backgroundColor: colorBarValues[0],
        //         width: "30px",
        //         marginLeft: "2rem"
        //       }}
        //     />
        //     <span style={{ margin: "0 1rem" }}>
        //       {Math.round(
        //         tm30.Numerical_Results.local_chroma_shift[0]
        //       )}
        //       %
        //     </span>
        //   </div>
        // ),
        lhs: tm30.Numerical_Results.local_hue_shift[0].toFixed(2),
        lcf: Math.round(tm30.Numerical_Results.local_color_fidelity[0]),
        children: [
          {
            lcs: (
              <div>
                {colorBarValues.map((color, childIndex) => (
                  <div key={color} className='color-bar'>
                    <Badge
                      count={childIndex + 1}
                      style={{
                        backgroundColor: color,
                        width: '30px',
                      }}
                    />
                    <span className='color-bar-value'>
                      {Math.round(
                        tm30.Numerical_Results.local_chroma_shift[childIndex]
                      )}
                      %
                    </span>
                  </div>
                ))}
              </div>
            ),
            lhs: (
              <div>
                {colorBarValues.map((color, childIndex) => (
                  <div key={color} className='color-bar'>
                    <Badge
                      count={childIndex + 1}
                      style={{
                        backgroundColor: color,
                        width: '30px',
                      }}
                    />
                    <span className='color-bar-value'>
                      {tm30.Numerical_Results.local_hue_shift[
                        childIndex
                      ].toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            ),
            lcf: (
              <div>
                {colorBarValues.map((color, childIndex) => (
                  <div key={color} className='color-bar'>
                    <Badge
                      count={childIndex + 1}
                      style={{
                        backgroundColor: color,
                        width: '30px',
                      }}
                    />
                    <span className='color-bar-value'>
                      {Math.round(
                        tm30.Numerical_Results.local_color_fidelity[childIndex]
                      )}
                    </span>
                  </div>
                ))}
              </div>
            ),
          },
        ],
        preferenceLevel: getPreferenceLevel(tm30.Numerical_Results),
        vividnessLevel: getVividnessLevel(tm30.Numerical_Results),
        fidelityLevel: getFidelityLevel(tm30.Numerical_Results),
        scf: (
          <a
            href='#'
            onClick={() => {
              setReportType('scf');
              setModalReportData(tm30.Numerical_Results);
            }}
          >
            View
          </a>
        ),
        report: (
          <ReadOutlined
            style={{ fontSize: '24px', textAlign: 'center' }}
            onClick={() => generateReport(libraryItem)}
          />
        ),
        delete: (
          <DeleteOutlined
            style={{ fontSize: '24px' }}
            onClick={() => setDeleteId(id)}
          />
        ),
        ...luoxTableData,
        reportsToDownload: null,
      };
      dataSource.push(tableEntry);
    });

    return dataSource;
  }, [libraryData]);

  const getFilteredTableColumns = useMemo(() => {
    if (columnVisibility) {
      const { tm30DataColumns, metadataColumns, luoxDataColumns } =
        columnVisibility || {};
      const combinedColumnVisibility = [
        ...tm30DataColumns,
        ...metadataColumns,
        ...luoxDataColumns,
      ];
      const filteredTableColumns = columns.filter((column) =>
        column.matchColumnTitle
          ? combinedColumnVisibility.includes(column.matchColumnTitle)
          : combinedColumnVisibility.includes(column.title)
      );

      // If example library, don't show delete column
      if (libraryType === 'example') {
        let deleteColumnIndex;
        filteredTableColumns.forEach((column, index) => {
          if (column.key === 'delete') deleteColumnIndex = index;
        });
        filteredTableColumns.splice(deleteColumnIndex, 1);
      }
      return filteredTableColumns;
    }
  }, [columns, libraryType, columnVisibility]);

  // Filtering Data
  const filteredDataSource = useMemo(() => {
    const filterData = (dataSource, filterDataKey) => {
      const filteredData = dataSource.filter((data) => {
        if (
          ['preferenceLevel', 'vividnessLevel', 'fidelityLevel'].includes(
            filterDataKey
          )
        ) {
          return tableFilterData[filterDataKey].includes(data[filterDataKey]);
        } else if (filterDataKey === 'cct') {
          const compareCctItem =
            typeof data[filterDataKey].props.children === 'object'
              ? data[filterDataKey].props.children.props.children
              : data[filterDataKey].props.children;
          return (
            compareCctItem >= tableFilterData[filterDataKey][0] &&
            compareCctItem <= tableFilterData[filterDataKey][1]
          );
        } else {
          return (
            data[filterDataKey] >= tableFilterData[filterDataKey][0] &&
            data[filterDataKey] <= tableFilterData[filterDataKey][1]
          );
        }
      });
      return filteredData;
    };

    let filteredDataSource = memoizedDataSource;
    if (Object.keys(tableFilterData).length !== 0) {
      Object.keys(tableFilterData).forEach((filterDataKey) => {
        filteredDataSource = filterData(filteredDataSource, filterDataKey);
      });
    }
    return filteredDataSource;
  }, [memoizedDataSource, tableFilterData]);

  const rowSelection = {
    // checkStrictly: true,
    onChange: handleImageDownloadIds,
    selectedRowKeys: imageDownloadIds,
  };

  const paginationConfig = {
    showQuickJumper: {
      goButton: <Button className='goto-button'>Go To</Button>,
    },
    position: ['bottomLeft'],
    pageSizeOptions: [10, 20, 50, 100, filteredDataSource.length],
    showTotal: (total, range) =>
      `Showing ${range[0]} to ${range[1]} of ${total} entries${
        total === libraryData.length
          ? ''
          : ` (filtered from ${libraryData.length} total entries)`
      }`,
  };

  const downloadDropdownMenu = (
    <Menu>
      <Menu.Item
        key='1'
        onClick={() => setImageDownloadModal(true)}
        disabled={imageDownloadIds.length === 0}
      >
        <Tooltip
          placement='right'
          title='Select rows to download'
          trigger={imageDownloadIds.length === 0 ? 'hover' : []}
        >
          Image
        </Tooltip>
      </Menu.Item>
      {/* <Menu.Item
        key="2"
        disabled={filteredDataSource.length === 0}
        onClick={() => setJsonDownloadModal(true)}
      >
        JSON
      </Menu.Item> */}
      <SubMenu title='JSON'>
        <Menu.Item
          key='2'
          onClick={() => {
            setJsonDownloadModal(true);
            setJsonDownloadType('full');
          }}
        >
          Full
        </Menu.Item>
        <Menu.Item
          key='3'
          onClick={() => {
            setJsonDownloadModal(true);
            setJsonDownloadType('filtered');
          }}
          disabled={Object.keys(tableFilterData).length === 0}
        >
          <Tooltip
            placement='right'
            title='Use the filter button to filter data'
            trigger={imageDownloadIds.length === 0 ? 'hover' : []}
          >
            Filtered
          </Tooltip>
        </Menu.Item>
        <Menu.Item
          key='4'
          onClick={() => {
            setJsonDownloadModal(true);
            setJsonDownloadType('checked');
          }}
          disabled={imageDownloadIds.length === 0}
        >
          <Tooltip
            placement='right'
            title='Select rows to download'
            trigger={imageDownloadIds.length === 0 ? 'hover' : []}
          >
            Checked
          </Tooltip>
        </Menu.Item>
      </SubMenu>
      <SubMenu title='SPDx'>
        <Menu.Item
          key='5'
          onClick={() => {
            setSpdxDownloadModal(true);
            setSpdxDownloadType('full');
          }}
        >
          Full
        </Menu.Item>
        <Menu.Item
          key='6'
          onClick={() => {
            setSpdxDownloadModal(true);
            setSpdxDownloadType('checked');
          }}
          disabled={imageDownloadIds.length === 0}
        >
          <Tooltip
            placement='right'
            title='Select rows to download'
            trigger={imageDownloadIds.length === 0 ? 'hover' : []}
          >
            Checked
          </Tooltip>
        </Menu.Item>
      </SubMenu>
    </Menu>
  );

  return (
    <>
      <div style={tableSpinner ? { opacity: 0.3, pointerEvents: 'none' } : {}}>
        <br />
        {imageDownloadStart &&
          libraryData.map((item) => {
            if (imageDownloadIds.includes(item.id)) {
              return (
                <div
                  style={{
                    position: 'absolute',
                    // right: '15rem',
                    // width: '110rem',
                    // bottom: 10,
                    opacity: 0,
                    pointerEvents: 'none',
                  }}
                >
                  {' '}
                  {imageDownloadType.includes('simple') && (
                    <div id={`MULTIPLE_DOWNLOAD_SIMPLE_${item.id}`}>
                      <Simple tm30={item.tm30.Numerical_Results} />
                    </div>
                  )}
                  {imageDownloadType.includes('intermediate') && (
                    <div id={`MULTIPLE_DOWNLOAD_INTERMEDIATE_${item.id}`}>
                      <Intermediate
                        tm30={item.tm30.Numerical_Results}
                        downloading={tableSpinner}
                      />
                    </div>
                  )}
                  {imageDownloadType.includes('full') && (
                    <div id={`MULTIPLE_DOWNLOAD_FULL_${item.id}`}>
                      <Full
                        reportData={{
                          tm30: item.tm30,
                          metadata: item.metadata,
                        }}
                        showMetadataActions={false}
                        downloading={tableSpinner}
                      />
                    </div>
                  )}
                </div>
              );
            }
          })}
        <Button
          style={{ marginBottom: '10px', marginRight: '10px' }}
          type='primary'
          onClick={() => setShowChooseColumnsModal(true)}
        >
          Show/Hide Columns
        </Button>
        {filteredDataSource.length !== 0 && (
          <>
            <Button
              style={{ marginRight: '10px' }}
              onClick={() => setTableFiltering(true)}
              type='primary'
              ghost
            >
              Filter
            </Button>
            <Dropdown trigger='click' overlay={downloadDropdownMenu}>
              <Button style={{ marginRight: '10px' }} type='primary' ghost>
                Download <DownOutlined />
              </Button>
            </Dropdown>
          </>
        )}
        {libraryType === 'user' && libraryData.length !== 0 && (
          <div style={{ float: 'right' }}>
            {['show', 'visited'].includes(showUploadError) && (
              <ExclamationCircleOutlined
                style={{ marginRight: '1rem', fontSize: '2rem', color: 'red' }}
                onClick={() => setOpenExcelUploadErrors(true)}
              />
            )}
            <Button
              style={{ marginRight: '1rem' }}
              type='primary'
              onClick={() => setAddLibrary(true)}
              disabled={showGlobalSpinner}
            >
              Add more SPDs
            </Button>
            <Button danger onClick={() => setClearLibrary(true)}>
              Clear Library
            </Button>
          </div>
        )}
        <ChooseColumnsModal
          showChooseColumnsModal={showChooseColumnsModal}
          setShowChooseColumnsModal={setShowChooseColumnsModal}
          setColumnVisibility={setColumnVisibility}
        />
        <ReportPreviewModal
          reportType={reportType}
          modalReportData={modalReportData}
          setReportType={setReportType}
          setModalReportData={setModalReportData}
        />
        <TableFilterModal
          tableFiltering={tableFiltering}
          tableFilterData={tableFilterData}
          memoizedDataSource={memoizedDataSource}
          setTableFiltering={setTableFiltering}
          setTableFilterData={setTableFilterData}
        />
        <JsonDownloadDetailsModal
          jsonDownloadType={jsonDownloadType}
          jsonDownloadModal={jsonDownloadModal}
          jsonDownloadName={jsonDownloadName}
          libraryData={libraryData}
          filteredDataSource={filteredDataSource}
          imageDownloadIds={imageDownloadIds}
          setImageDownloadIds={setImageDownloadIds}
          setJsonDownloadModal={setJsonDownloadModal}
          setJsonDownloadName={setJsonDownloadName}
        />
        <MultipleSpdxDownloadModal
          spdxDownloadModal={spdxDownloadModal}
          setSpdxDownloadModal={setSpdxDownloadModal}
          spdxDownloadName={spdxDownloadName}
          setSpdxDownloadName={setSpdxDownloadName}
          libraryData={libraryData}
          imageDownloadIds={imageDownloadIds}
          spdxDownloadType={spdxDownloadType}
          setImageDownloadIds={setImageDownloadIds}
        />
        <DeleteLibraryItemModal deleteId={deleteId} setDeleteId={setDeleteId} />
        <ClearLibraryModal
          clearLibrary={clearLibrary}
          setClearLibrary={setClearLibrary}
        />
        <AddLibraryModal
          addLibrary={addLibrary}
          setAddLibrary={setAddLibrary}
        />
        <UploadErrorModal
          libraryType={libraryType}
          openExcelUploadErrors={openExcelUploadErrors}
          setOpenExcelUploadErrors={setOpenExcelUploadErrors}
        />
        <div className={showGlobalSpinner ? showGlobalSpinner : ''}>
          <LoadingScreen
            show={['initialize', 'globalSpin'].includes(showGlobalSpinner)}
          />
        </div>
        <ConfigProvider
          renderEmpty={filteredDataSource.length === 0 && renderTableEmpty}
        >
          <Table
            className={
              ['initialize'].includes(showGlobalSpinner) ? 'add-opacity' : ''
            }
            scroll={{ x: 1500, y: 600 }}
            sticky
            // expandable
            // expandable={{
            //   rowExpandable: (record) =>
            //     columnVisibility?.tm30DataColumns.includes(
            //       "Local Chroma Shift (RCS,hj)"
            //     ) ||
            //     columnVisibility?.tm30DataColumns.includes(
            //       "Local Hue Shift (Rhs,hj)"
            //     ) ||
            //     columnVisibility?.tm30DataColumns.includes(
            //       "Local Color Fidelity (Rf,hj)"
            //     ) ||
            //     false,
            // }}
            // expandIconColumnIndex={
            //   checkAllMetaData ? 18 : columnVisibility.indexOf("expand") - 1
            // }
            bordered={true}
            pagination={paginationConfig}
            rowSelection={rowSelection}
            // loading={tableLoading}
            columns={getFilteredTableColumns}
            // dataSource={tableDataSource}
            dataSource={filteredDataSource}
            onChange={(pagination, filters, sorter, extra) => {
              // console.log(sorter);
              // sessionStorage.setItem('table-sort', JSON.stringify(sorter));
              // setTableDataAfterFiltersAndSorters({
              //   sorter,
              //   data: extra.currentDataSource,
              // });
            }}
          />
        </ConfigProvider>
      </div>
      <ImageDownloadOptionsModal
        imageDownloadIds={imageDownloadIds}
        imageDownloadName={imageDownloadName}
        imageDownloadType={imageDownloadType}
        imageDownloadModal={imageDownloadModal}
        libraryData={libraryData}
        setImageDownloadIds={setImageDownloadIds}
        setImageDownloadStart={setImageDownloadStart}
        setImageDownloadName={setImageDownloadName}
        setImageDownloadModal={setImageDownloadModal}
        setImageDownloadType={setImageDownloadType}
        setTableSpinner={setTableSpinner}
      />
    </>
  );
};

export default LibraryTable;
